import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaArrowRight, FaTimes } from "react-icons/fa";
import styled from "styled-components";
import { analytics } from "MVVM/Analytics";
import background from "MVVM/Assets/Homepage/referral-rewards.webp";
import { CONTENT_MAX_WIDTH } from "./styledComponents";

const DISMISSED_KEY = "referralAnnouncementDismissed";
const promotionName = "partner-referral-program";

export const ReferralAnnouncement = () => {
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISSED_KEY) === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (!dismissed) {
      analytics.track("Homepage Announcement - View", { promotionName });
    }
  }, [dismissed]);

  if (dismissed) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISSED_KEY, "1");
    } catch {
      // The banner can still be dismissed when storage is unavailable.
    }
    setDismissed(true);
    analytics.track("Homepage Announcement - Dismiss", { promotionName });
  };

  return (
    <Banner aria-labelledby="referral-announcement-title">
      <Content>
        <Eyebrow>
          Affiliate Program <Beta>Beta</Beta>
        </Eyebrow>
        <Title id="referral-announcement-title">
          Your invites come with rewards.
        </Title>
        <Description>
          Earn subscription credit or cash when friends subscribe to Allstar.
        </Description>
        <Cta
          to="/referrals"
          onClick={() =>
            analytics.track("Homepage Announcement - Click", { promotionName })
          }
        >
          Start referring <FaArrowRight aria-hidden="true" size={13} />
        </Cta>
      </Content>
      <Dismiss
        type="button"
        onClick={dismiss}
        aria-label="Dismiss referral announcement"
      >
        <FaTimes aria-hidden="true" size={13} />
      </Dismiss>
    </Banner>
  );
};

const Banner = styled.section`
  align-self: center;
  background:
    linear-gradient(rgba(6, 16, 23, 0.12), rgba(6, 16, 23, 0.35)),
    url(${background}) center / cover;
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 12px;
  box-sizing: border-box;
  margin: 0 auto;
  max-width: ${CONTENT_MAX_WIDTH};
  overflow: hidden;
  padding: 34px 24px;
  position: relative;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breaks.small}) {
    background-image: linear-gradient(
        rgba(6, 16, 23, 0.6),
        rgba(6, 16, 23, 0.75)
      ),
      url(${background});
    padding: 38px 20px 28px;
  }
`;

const Content = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  max-width: 660px;
  text-align: center;
`;

const Eyebrow = styled.div`
  align-items: center;
  color: ${({ theme }) => theme.colors.ice};
  display: flex;
  flex-wrap: wrap;
  font-size: 12px;
  font-weight: 700;
  gap: 8px;
  justify-content: center;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const Beta = styled.span`
  background: ${({ theme }) => theme.colors.envyAlpha20};
  border: 1px solid ${({ theme }) => theme.colors.envyAlpha40};
  border-radius: 100px;
  font-size: 10px;
  padding: 3px 8px;
`;

const Title = styled.h2`
  color: ${({ theme }) => theme.colors.ice};
  font-size: clamp(26px, 3vw, 38px);
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.1;
  margin: 10px 0 0;
`;

const Description = styled.p`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 16px;
  line-height: 1.5;
  margin: 12px 0 20px;
`;

const Cta = styled(Link)`
  align-items: center;
  background: ${({ theme }) => theme.colors.tierPlat};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.ice};
  display: inline-flex;
  font-size: 15px;
  font-weight: 700;
  gap: 12px;
  min-height: 44px;
  padding: 0 22px;
  text-decoration: none;

  &:hover {
    color: ${({ theme }) => theme.colors.ice};
    filter: brightness(1.12);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.ice};
    outline-offset: 4px;
  }
`;

const Dismiss = styled.button`
  align-items: center;
  background: rgba(6, 16, 23, 0.65);
  border: none;
  border-radius: 50%;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  display: flex;
  height: 36px;
  justify-content: center;
  position: absolute;
  right: 8px;
  top: 8px;
  width: 36px;

  &:hover,
  &:focus-visible {
    background: ${({ theme }) => theme.colors.navy300};
    outline: 2px solid ${({ theme }) => theme.colors.ice};
  }
`;
